<template>
  <div 
    v-if="showProgressFooter" 
    class="fixed-progress-footer"
    :class="{ 'marketing-footer': isMarketingRoute }"
  >
    <div class="progress-container">
      <div class="progress-bar-wrapper">
        <ProgressBar 
          :progressFooter="true" 
          :progressStats="progressStats"
          :activeTask="activeTask"
          :isOnCurrentTaskPage="isOnCurrentTaskPage"
          :smartButtonText="smartButtonText"
          :displayTask="displayTask"
          @continueClick="handleContinueClick"
        />
      </div>
    </div>
    <book-appointment-dialog
      v-model="showBookAppointmentDialog"
      :calendly-url="bookAppointmentCalendlyUrl"
      @scheduled="handleBookAppointmentScheduled"
      @closed="handleBookAppointmentClosed"
    />
    <v-snackbar v-model="showLessonConfigSnackbar" timeout="8000" bottom color="info">
      {{ lessonConfigMessage }}
    </v-snackbar>
    <university-lesson-video-modal
      v-if="showUniversityModal"
      :video-url="universityVideoUrl"
      @close="onUniversityModalClose"
      @videoCompleted="onUniversityVideoCompleted"
    />
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import onboardingMixin from '@/mixins/onboardingMixin'
import housejetplus from '@/mixins/housejetplus'
import constants from '@/mixins/constants'
import bookAppointmentTask from '@/mixins/bookAppointmentTask'
import videoModalMixin from '@/mixins/videoModalMixin'
import { purpleChartOptions } from '@/components/Charts/config';
import UniversityLessonVideoModal from '@/components/Onboarding/UniversityLessonVideoModal.vue'

const ProgressBar = () => import(/* webpackChunkName: "progress-bar" */ "@/components/DashboardNew/ProgressBar.vue")

/**
 * Progress footer component that displays onboarding progress and next task
 */
export default {
  name: 'ProgressFooter',
  components: {
    ProgressBar,
    UniversityLessonVideoModal,
  },

  mixins: [housejetplus, constants, onboardingMixin, bookAppointmentTask, videoModalMixin],

  data() {
    return {
      styleElement: null,
      pollOnboardingData: false,
      progressPollInterval: null,
      currentHash: window.location.hash, // Track hash changes reactively
      lastKnownHash: window.location.hash, // Fallback hash tracking
      showLessonConfigSnackbar: false,
      lessonConfigMessage:
        'This training task does not have a course lesson attached yet. Please contact your administrator.',
      showUniversityModal: false,
      universityVideoUrl: null,
    }
  },
  
  computed: {
    ...mapFields('onboarding_state', [
      'displayedFooter',
      'showModalForced',
      'onboardingData',
      'currentProgress',
      'currentLevel',
      'nextLevelWithAtLeastOneIncompleteTask',
    ]),
    ...mapFields('current_user', ['currentUser']),

    /**
     * Determine if the footer should be shown
     */
    shouldShowFooter() {
      return !!(this.onboardingData?.[0] && this.computedNextIncompleteTask);
    },

    showProgressFooter(){
      // Don't show footer on the dashboard (/agents) page or capacitor app
      if ( this.isDashboardPage || window.IS_MOBILE_APP ) {
        return false;
      }

      return this.shouldShowFooter
    },

    isMarketingRoute() {
      return window.location.pathname.includes('/house-jet/marketing') || window.location.pathname.includes('/lead-config/marketing');;
    },

    /**
     * Check if current route is the dashboard page (/agents)
     */
     isDashboardPage() {
      return this.$route?.name === 'dashboard';
    },

    /**
     * Get current page path for comparison
     */
    currentPagePath() {
      const pathname = window.location.pathname.replace(/^\/|\/$/g, '');
      const hash = this.currentHash.replace(/^#/, '');
      return (pathname + (hash ? '#' + hash : '')).trim();
    },

    /**
     * Get progress statistics for footer display
     */
    progressStats() {
      if (!this.onboardingData?.[0]) return null;

      let completedTasks = 0;
      let totalTasks = 0;
      
      this.onboardingData.forEach(level => {
        level?.features?.forEach(feature => {
          feature?.tasks?.forEach(task => {
            if(!level.level_complete && !level.is_locked){
              totalTasks++;
              if (task.complete || task.skipped) completedTasks++;
            }
          });
        });
      });

      return {
        completed: completedTasks,
        total: totalTasks
      };
    },

    /**
     * Find the next incomplete task across all levels
     */
    computedNextIncompleteTask() {
      if (!this.onboardingData) return null;

      // First check current level
      for (const level of this.onboardingData) {
        for (const feature of level.features) {
          for (const task of feature.tasks) {
            if (!task.complete && !task.skipped) {
              return task;
            }
          }
        }
      }

      // If all tasks in current level are complete, check next level
      if (this.nextLevelWithAtLeastOneIncompleteTask?.features?.length > 0) {
        for (const feature of this.nextLevelWithAtLeastOneIncompleteTask.features) {
          if (feature.tasks?.length > 0) {
            for (const task of feature.tasks) {
              if (!task.complete && !task.skipped) {
                return task;
              }
            }
          }
        }
      }

      return null;
    },

    /**
     * Check if user is currently on the first incomplete task's page
     */
    isOnCurrentTaskPage() {
      const currentTask = this.computedNextIncompleteTask;
      return currentTask && this.isOnTaskRoute(currentTask.link);
    },

    /**
     * Find the task that comes after the current incomplete task
     */
    nextTaskAfterCurrent() {
      const currentTask = this.computedNextIncompleteTask;
      if (!this.onboardingData || !currentTask) return null;

      let foundCurrent = false;
      
      for (const level of this.onboardingData) {
        for (const feature of level.features) {
          for (const task of feature.tasks) {
            if (foundCurrent && !task.complete && !task.skipped) {
              return task;
            }
            if (task.id === currentTask.id) {
              foundCurrent = true;
            }
          }
        }
      }

      return null; // No task found after the current task
    },

    /**
     * Get the appropriate button text based on current state
     */
    smartButtonText() {
      const currentTask = this.computedNextIncompleteTask;
      if (!currentTask) return 'Continue';

      if (this.isOnCurrentTaskPage) {
        const upNext = this.nextTaskAfterCurrent;
        if (upNext) {
          return `Up Next: ${upNext.name}`;
        } else {
          return 'Completed';
        }
      }

      // Default case: show the current incomplete task name
      return currentTask.name;
    },

    /**
     * Check if there's a task to display
     */
    displayTask() {
      return this.computedNextIncompleteTask !== null;
    },

    /**
     * Get the active task
     */
    activeTask() {
      return this.computedNextIncompleteTask;
    },
  },

  mounted() {
    this.$root.$on('onboarding-lesson-video-completed', this.onOnboardingLessonVideoCompleted);
    //this.injectStyles();
    
    // Listen for hash changes
    window.addEventListener('hashchange', this.handleHashChange);
    
    // Also check for hash changes during polling as a fallback
    this.lastKnownHash = window.location.hash;
  },

  methods: {
    /**
     * Whether the app is already on the route described by a task link.
     */
    isOnTaskRoute(link) {
      if (!link) {
        return false;
      }

      const normalized = link.startsWith('/') ? link : `/${link}`;
      const [pathPart, hashPart] = normalized.split('#');
      const currentPath = (this.$route?.path || window.location.pathname).replace(/\/$/, '');
      const targetPath = pathPart.replace(/\/$/, '');

      if (currentPath !== targetPath) {
        return false;
      }

      if (hashPart) {
        const expectedHash = hashPart.startsWith('#') ? hashPart : `#${hashPart}`;
        return (this.$route?.hash || window.location.hash) === expectedHash;
      }

      return true;
    },

    /**
     * Navigate to a task link without throwing NavigationDuplicated.
     */
    async navigateToTaskLink(link) {
      if (!link) {
        return;
      }

      const normalized = link.startsWith('/') ? link : `/${link}`;
      const [pathPart, hashPart] = normalized.split('#');

      if (this.isOnTaskRoute(link)) {
        return;
      }

      const location = hashPart
        ? { path: pathPart, hash: hashPart.startsWith('#') ? hashPart : `#${hashPart}` }
        : pathPart;

      try {
        await this.$router.push(location);
      } catch (err) {
        if (err?.name !== 'NavigationDuplicated' && !String(err?.message || '').includes('NavigationDuplicated')) {
          throw err;
        }
      }
    },
    /**
     * Check if a given path exists in Vue Router
     * @param {string} path - The path to check (with or without leading slash)
     * @returns {boolean} - True if route exists in Vue Router
     */
     isVueRoute(path) {
      if (!path) return false;
      
      // Normalize the path - ensure it starts with /
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      
      // Split path and query string
      const [pathWithoutQuery] = normalizedPath.split('?');
      
      try {
        // Try to resolve the route
        const resolved = this.$router.resolve(pathWithoutQuery);
        
        // Check if route was matched (not redirected to 404 or catch-all)
        return resolved && 
               resolved.route && 
               resolved.route.matched.length > 0 &&
               resolved.route.name !== '404-page';
      } catch (error) {
        console.warn('Error checking route:', error);
        return false;
      }
    },

    /**
     * Initialize the footer component
     */
    async init() {
      if (!this.currentUser?.id) {
        return;
      }

      if (this.activeRequest) {
        await this.activeRequest;
      }

      if (!this.onboardingData?.[0]) {
        await this.fetchOnboardingDataSafely();
      }
      
      // Check if we should show the footer
      if (this.shouldShowFooter && !this.pollOnboardingData) {
        await this.startProgressPolling();
      }
    },

    /**
     * Start polling for progress updates
     */
    async startProgressPolling() {
      await this.fetchOnboardingDataSafely();

      this.pollOnboardingData = true;

      if (this.progressPollInterval) {
        clearInterval(this.progressPollInterval);
      }

      console.log('startProgressPolling');

      this.progressPollInterval = setInterval(async () => {
        await this.fetchOnboardingDataSafely();
        
        // Check for hash changes as fallback
        if (this.lastKnownHash !== window.location.hash) {
          this.lastKnownHash = window.location.hash;
          this.currentHash = window.location.hash;
        }
        
        if ( !this.shouldShowFooter ) {
          this.cleanup();
        }
      }, 6000);
    },

    /**
     * Safely fetch onboarding data with error handling
     */
    async fetchOnboardingDataSafely() {
      try {
        await this.getOnboardingData();
      } catch (error) {
        console.error('Error fetching onboarding data:', error);
      }
    },

    /**
     * Clean up polling and hide footer
     */
    cleanup() {
      this.pollOnboardingData = false;
      
      if (this.progressPollInterval) {
        clearInterval(this.progressPollInterval);
        this.progressPollInterval = null;
      }
      
      //this.removeStyles();
      this.$emit('remove');
    },

    /**
     * Handle continue button click
     */
    async handleContinueClick() {
      const task = this.activeTask;
      if (!task) return;

      if(task.task_type_key === this.TASK_TYPE_GATHER_BILLING_INFO) {
        this.showHouseJetPlusDialog({'key_type' : 'onboarding'}, true);
        return;
      }

      if (task.task_type_key === this.TASK_TYPE_BOOK_APPOINTMENT) {
        this.completeBookAppointmentAndOpenModal(task);
        return;
      }

      if (task.task_type_key === this.TASK_TYPE_UNIVERSITY_COURSE_LESSON) {
        if (task.complete) {
          return;
        }
        if (task.lesson_video_url && task.university_lesson_id) {
          this.$store.commit('onboarding_state/setVideoCompleted', false);
          this.currentLessonData = {
            videoUrl: task.lesson_video_url,
            lessonId: task.university_lesson_id,
            taskId: task.id,
            isCompleted: false,
            video_cta_label: task.video_cta_label || 'Watch the training video',
          };
          this.universityVideoUrl = task.lesson_video_url;
          this.showUniversityModal = true;
          return;
        }
        this.showLessonConfigSnackbar = true;
        return;
      }

      if (task.university_lesson_id && this.isOnTaskRoute(task.link)) {
        this.showModalForced = true;
        return;
      }

      if (!task.link) {
        return;
      }

      await this.navigateToTaskLink(task.link);
    },
    /**
     * Refreshes task list after a university video marks the lesson complete via API.
     */
    onOnboardingLessonVideoCompleted() {
      this.fetchOnboardingDataSafely();
    },
    /**
     * Closes the university lesson modal without triggering completion.
     */
    onUniversityModalClose() {
      this.showUniversityModal = false;
      this.universityVideoUrl = null;
    },
    /**
     * Called when the university lesson video has been fully watched.
     * Hides the modal and broadcasts the completion event to refresh onboarding state.
     */
    onUniversityVideoCompleted() {
      this.showUniversityModal = false;
      this.universityVideoUrl = null;
      this.$root.$emit('onboarding-lesson-video-completed');
    },
    /**
     * Handle hash change events
     */
    handleHashChange() {
      this.currentHash = window.location.hash;
    },

    /**
     * Manages the dynamic injection and removal of footer styles
     */
    injectStyles() {
      const styles = `
        .kt-grid.kt-grid--hor.kt-grid--root {
          padding-bottom: 0px !important;
        }
        @media screen and (max-width: 600px) {
          .kt-grid.kt-grid--hor.kt-grid--root {
            padding-bottom: 30px !important;
          }
        }

        /* Styles for save changes button when footer is present */
        .class-for-fixed-progress-footer {
          bottom: 120px !important;
          height: 34px !important;
          max-width: 164px !important;
          position: fixed !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          padding-top: 0 !important;
        }

        .class-for-fixed-progress-footer .v-btn {
          max-width: 164px !important;
          width: 100% !important;
          margin: 0 auto !important;
        }

        /* Default state for kt-scrolltop when footer is present */
        .kt-scrolltop {
          bottom: 80px !important;
        }

        /* Mobile design for screens smaller than 600px */
        @media screen and (max-width: 600px) {

          .kt-scrolltop {
            bottom: 120px !important;
          }
        }
      `;

      if (!this.styleElement) {
        this.styleElement = document.createElement('style');
        this.styleElement.id = 'progress-footer-styles-' + this._uid;
        this.styleElement.textContent = styles;
        document.head.appendChild(this.styleElement);
      }
    },

    removeStyles() {
      if (this.styleElement) {
        this.styleElement.remove();
        this.styleElement = null;
      }
    }
  },

  watch: {
    'currentUser.id': {
      immediate: true,
      handler(userId) {
        if (userId) {
          this.init();
        }
      },
    },
    shouldShowFooter(isVisible) {
      if (isVisible && this.currentUser?.id && !this.pollOnboardingData) {
        this.startProgressPolling();
      }
    },
    /**
     * Watch for changes in the next incomplete task
     */
    computedNextIncompleteTask(newVal) {
      if (!newVal) {
        this.cleanup();
      }
    }
  },

  beforeDestroy() {
    this.$root.$off('onboarding-lesson-video-completed', this.onOnboardingLessonVideoCompleted);
    if (this.progressPollInterval) {
      clearInterval(this.progressPollInterval);
    }
    this.removeStyles();
    
    // Clean up event listener
    window.removeEventListener('hashchange', this.handleHashChange);
  },
}
</script>

<style scoped>
/* Keep component-specific styles here */
.fixed-progress-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--secondary-background);
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  z-index: 7;
  padding: 1rem;
  transition: transform 0.3s ease;
}

.marketing-footer {
  z-index: 1000;
}

.progress-container {
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: nowrap; /* Keep elements in one row by default */
  max-width: 1200px;
}

.progress-bar-wrapper {
  flex: 1 1 auto;
  min-width: 100px;
  height: 25px;
  background: var(--secondary-background) !important;
  border-radius: var(--border-radius-secondary) !important;
  overflow: hidden;
  position: relative;
}

.progress-bar {
  height: 100%;
  background: var(--primary-color);
  transition: width 0.3s ease;
  position: relative;
}

.progress-percentage {
  color: black;
  font-weight: 600;
  font-size: 12px;
  position: absolute;
  text-shadow: 0 0 2px rgba(0,0,0,0.5);
  left: 50%;
  transform: translateX(-50%);
  min-width: 40px;
  text-align: center;
  z-index: 1;
  top: 50%;
  transform: translate(-50%, -50%);
}



@media screen and (max-width: 960px) {
  .progress-bar-wrapper {
    width: 100%;
    order: 3;
    height: 70px;
  }
}
/* Mobile design for screens smaller than 600px */
@media screen and (max-width: 600px) {

  .progress-container {
    flex-wrap: wrap;
    gap: 0.75rem;
  }

  .progress-text {
    width: 100%;
    text-align: center;
    font-size: 12px;
  }

  .continue-btn, .completion-message {
    width: 100%;
    text-align: center;
    order: 2;
  }

  .fixed-progress-footer {
    padding: 0.75rem;
  }
}
</style> 